import React, { useState } from 'react';
import {
  Users, TrendingUp, Zap, CheckCircle, Phone, Mail, MapPin,
  User, Briefcase, GraduationCap, Calendar, DollarSign, Target, Award, Handshake, Clock
} from 'lucide-react';

const AssociateProgram = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    occupation: '',
    degree: '',
    postalAddress: '',
    state: 'Uttar Pradesh',
    city: '',
    pincode: ''
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Associate application:', formData);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  return (
    <div className="bg-gray-50">
      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-green-800 to-emerald-700 text-white py-16 md:py-24 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 animate-fade-in-up">
              Associate Program
            </h1>
            <p className="text-xl md:text-2xl mb-8 animate-fade-in-up animation-delay-200">
              Increase your earning by becoming a CashPro Financial Services Associate
            </p>
            <button className="bg-yellow-400 hover:bg-yellow-300 text-gray-800 font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg animate-fade-in-up animation-delay-400">
              Apply Now
            </button>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-12">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="white"></path>
          </svg>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Opportunity Overview with New Image */}
        <div className="mb-16 bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 items-center">
            {/* Image container – fixed to show full image */}
            <div className="flex justify-center items-center p-4 bg-gray-50">
              <img
                src="associate.png"
                alt="Who can become an associate – professions"
                className="w-full h-auto max-h-[500px] object-contain rounded-lg"
              />
            </div>

            <div className="p-6 md:p-10">
              <span className="inline-block bg-green-100 text-green-700 text-sm font-semibold px-4 py-1 rounded-full mb-4">
                Open for All Professionals
              </span>

              <h2 className="text-3xl md:text-4xl font-bold text-green-700 mb-4 leading-tight">
                Who Can Become an Associate With Us?
              </h2>

              <p className="text-gray-600 text-base md:text-lg mb-6 leading-relaxed">
                Whether you're a working professional, freelancer, or self‑employed expert, 
                our associate program welcomes a wide range of backgrounds. No investment 
                required – just a passion for helping people with the right financial solutions.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="flex items-start gap-3 bg-green-50 p-4 rounded-xl">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Diverse Backgrounds</h3>
                    <p className="text-sm text-gray-600">Builders, CAs, bankers, agents & more</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-green-50 p-4 rounded-xl">
                  <Clock className="w-5 h-5 text-green-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Flexible Engagement</h3>
                    <p className="text-sm text-gray-600">Work part‑time or full‑time</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-green-50 p-4 rounded-xl">
                  <Zap className="w-5 h-5 text-green-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Quick Payouts</h3>
                    <p className="text-sm text-gray-600">Commission within 7 days*</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-green-50 p-4 rounded-xl">
                  <DollarSign className="w-5 h-5 text-green-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Zero Investment</h3>
                    <p className="text-sm text-gray-600">Start earning without any cost</p>
                  </div>
                </div>
              </div>

              <button className="bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg">
                Apply Now
              </button>
            </div>
          </div>
        </div>

        {/* How to Become Associate */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">
            How to Become CashPro Financial Services Associate
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { step: "1", title: "Fill Form", desc: "Fill the form given below." },
              { step: "2", title: "Receive Call", desc: "Receive a call from us for guidance." },
              { step: "3", title: "Complete Training", desc: "Complete the training process." },
              { step: "4", title: "Formalities", desc: "Complete other formalities." },
              { step: "5", title: "Become Associate", desc: "Become a CashPro Financial Services Associate." }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-md text-center hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Advantages */}
        <div className="mb-16 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">
            Advantages of CashPro Financial Services Associate
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-4 bg-white p-4 rounded-lg shadow">
              <DollarSign className="w-8 h-8 text-green-600" />
              <div>
                <h3 className="font-semibold text-gray-800">No investment needed</h3>
                <p className="text-sm text-gray-600">Zero cost to start</p>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-white p-4 rounded-lg shadow">
              <Clock className="w-8 h-8 text-green-600" />
              <div>
                <h3 className="font-semibold text-gray-800">Part‑time business</h3>
                <p className="text-sm text-gray-600">Manage easily alongside your job</p>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-white p-4 rounded-lg shadow">
              <Zap className="w-8 h-8 text-green-600" />
              <div>
                <h3 className="font-semibold text-gray-800">Instant Payment</h3>
                <p className="text-sm text-gray-600">Commission within 7 days*</p>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">How Does It Work?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { icon: Users, title: "Identify Client", desc: "Generate a lead by identifying a loan‑seeker, categorise client needs." },
              { icon: CheckCircle, title: "Eligibility Check", desc: "Check facts and parameters by gathering required information." },
              { icon: Handshake, title: "Share Lead", desc: "Share client details with us for further procedure." },
              { icon: DollarSign, title: "Loan Disbursal", desc: "Get loan disbursed for the client by preferred lender." },
              { icon: Award, title: "Commission Payout", desc: "Get your commission within 7 days of loan disbursal. *(T&C Apply)" }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-md text-center hover:shadow-xl transition-all">
                <item.icon className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Who Can Apply - expanded to match image */}
        <div className="mb-16 bg-white rounded-xl shadow-md p-8 text-center">
          <h2 className="text-3xl font-bold text-green-700 mb-4">Who Can Apply?</h2>
          <p className="text-gray-600 text-lg mb-6">
            The CashPro Associate Program is open to professionals from diverse fields:
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              "Builder", "CA", "DSA/DST", "Ex-banker", "Financial Analyst", "Financial Consultant",
              "Freelancer", "Insurance Advisor", "Loan Agent", "Mutual Fund Agent",
              "Real Estate Agent/Broker", "Tax Consultant", "Others"
            ].map(prof => (
              <span key={prof} className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium">
                {prof}
              </span>
            ))}
          </div>
          <p className="text-gray-500 text-sm mt-6">
            *If your profession is not listed, you are still welcome to apply.
          </p>
        </div>

        {/* Application Form - Two Column Layout */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Left side: Form */}
            <div className="p-6 md:p-8">
              <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Apply Now</h2>
              {submitted && (
                <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
                  Thank you! Your application has been submitted. Our team will connect with you shortly.
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input type="text" name="firstName" required value={formData.firstName} onChange={handleChange}
                        className="pl-10 w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input type="text" name="lastName" required value={formData.lastName} onChange={handleChange}
                        className="pl-10 w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">E-mail *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input type="email" name="email" required value={formData.email} onChange={handleChange}
                        className="pl-10 w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mobile *</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input type="tel" name="mobile" required value={formData.mobile} onChange={handleChange}
                        className="pl-10 w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Occupation *</label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input type="text" name="occupation" required value={formData.occupation} onChange={handleChange}
                        className="pl-10 w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Degree *</label>
                    <div className="relative">
                      <GraduationCap className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input type="text" name="degree" required value={formData.degree} onChange={handleChange}
                        className="pl-10 w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postal Address *</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                      <textarea name="postalAddress" rows="2" required value={formData.postalAddress} onChange={handleChange}
                        className="pl-10 w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                    <select name="state" value={formData.state} onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500">
                      <option>Uttar Pradesh</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                    <input type="text" name="city" required value={formData.city} onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pincode *</label>
                    <input type="text" name="pincode" required value={formData.pincode} onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:outline-none focus:ring-2 focus:ring-green-500" />
                  </div>
                </div>
                <div className="text-center">
                  <button type="submit" className="bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg">
                    Send
                  </button>
                  <p className="text-xs text-gray-500 mt-3">Our team will connect with you shortly.</p>
                </div>
              </form>
            </div>

            {/* Right side: Image */}
            <div className="bg-gray-50 flex items-center justify-center p-6 md:p-8">
              <img
                src="asso.png"
                alt="Become an Associate with CashPro"
                className="w-full h-auto max-h-[600px] object-contain rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up { animation: fade-up 0.6s ease-out forwards; }
        .animation-delay-200 { animation-delay: 0.2s; }
        .animation-delay-400 { animation-delay: 0.4s; }
      `}</style>
    </div>
  );
};

export default AssociateProgram;